import {
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  forwardRef,
  isValidElement,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Children } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/cn';

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function Field({ label, hint, error, required, children, className }: FieldProps) {
  return (
    <div className={cn('block', className)}>
      <label className="mb-1 block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      {children}
      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => (
  <input
    ref={ref}
    {...props}
    className={cn(
      'w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm transition focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50',
      className,
    )}
  />
));
Input.displayName = 'Input';

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    {...props}
    className={cn(
      'w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm transition focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
      className,
    )}
  />
));
Textarea.displayName = 'Textarea';

interface FlatOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

/** Flattens <option>/<optgroup> children (including conditional/.map()-built arrays and fragments) into a plain list — React.Children.forEach already walks nested arrays/fragments, we only need one extra level of recursion for <optgroup>. */
function flattenOptions(children: ReactNode): FlatOption[] {
  const result: FlatOption[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const props = child.props as Record<string, unknown>;
    if (child.type === 'option') {
      result.push({
        value: String(props.value ?? ''),
        label: props.children == null ? '' : String(props.children),
        disabled: !!props.disabled,
      });
    } else if (child.type === 'optgroup') {
      const label = String(props.label ?? '');
      for (const opt of flattenOptions(props.children as ReactNode)) {
        result.push({ ...opt, group: label });
      }
    } else if (props?.children) {
      result.push(...flattenOptions(props.children as ReactNode));
    }
  });
  return result;
}

/**
 * Searchable dropdown — same external API as a native <select> (value/
 * onChange/defaultValue/disabled/name/required, <option>/<optgroup>
 * children), so every existing call site (154 across the app, many via
 * react-hook-form's `{...register('field')}`) gets search with zero
 * changes elsewhere. Internally keeps a REAL, visually-hidden <select> as
 * the actual form-bound element — react-hook-form's uncontrolled
 * `register()` reads `ref.current.value` directly, which only a genuine
 * select element satisfies — and drives it via the browser's native value
 * setter + a dispatched `change` event so both that uncontrolled path AND
 * a plain `onChange` prop stay in sync. The visible trigger + search
 * popover is a separate, purely presentational layer on top.
 */
export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement>
>(({ className, children, onChange, value, defaultValue, disabled, ...props }, forwardedRef) => {
  const selectRef = useRef<HTMLSelectElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [search, setSearch] = useState('');
  // Mirrors the hidden select's value for uncontrolled usage (no `value`
  // prop passed) so the trigger label still updates on selection.
  const [uncontrolledValue, setUncontrolledValue] = useState(
    value !== undefined ? undefined : (defaultValue as string | undefined) ?? '',
  );

  useImperativeHandle(forwardedRef, () => selectRef.current as HTMLSelectElement, []);

  const options = useMemo(() => flattenOptions(children), [children]);
  const currentValue = value !== undefined ? String(value) : (uncontrolledValue ?? selectRef.current?.value ?? '');

  useEffect(() => {
    if (!open) return;
    function handlePointer(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handlePointer);
    return () => document.removeEventListener('mousedown', handlePointer);
  }, [open]);

  // Short option lists (page-size pickers, status enums, yes/no — anywhere
  // under this threshold) don't need a search box; showing one anyway just
  // adds a click-through step before the one option the user actually
  // wants is reachable. Longer lists (schools, students, classes — the
  // 154-call-site case this component was built for) keep it.
  const SEARCH_THRESHOLD = 7;
  const searchable = options.length > SEARCH_THRESHOLD;

  useEffect(() => {
    if (open) {
      setSearch('');
      // The popover is `position: absolute` within its own (usually
      // scrollable, e.g. DataTable's <main>) container — a trigger sitting
      // near the bottom of that container, like the pagination bar's
      // "Rows per page" picker, otherwise has its dropdown clipped by that
      // container's bottom edge rather than just reflowing onto the page.
      // Flip it to open upward whenever there isn't enough room below but
      // there's more room above.
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const POPOVER_EST_HEIGHT = 300;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        setOpenUpward(spaceBelow < POPOVER_EST_HEIGHT && spaceAbove > spaceBelow);
      }
      if (searchable) {
        // Let the popover mount before focusing.
        requestAnimationFrame(() => searchInputRef.current?.focus());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function selectOption(optionValue: string) {
    const el = selectRef.current;
    if (el) {
      const setter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value')?.set;
      setter ? setter.call(el, optionValue) : (el.value = optionValue);
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (value === undefined) setUncontrolledValue(optionValue);
    setOpen(false);
  }

  const term = search.trim().toLowerCase();
  const filtered = term ? options.filter((o) => o.label.toLowerCase().includes(term)) : options;
  const selected = options.find((o) => o.value === currentValue);

  // Preserve <optgroup> grouping visually in the filtered list.
  const groups: { label: string | null; options: FlatOption[] }[] = [];
  for (const opt of filtered) {
    const last = groups[groups.length - 1];
    if (last && last.label === (opt.group ?? null)) last.options.push(opt);
    else groups.push({ label: opt.group ?? null, options: [opt] });
  }

  return (
    <div ref={containerRef} className="relative">
      {/* The real form-bound element — invisible, but a genuine <select> so
          react-hook-form's ref-based uncontrolled reads keep working. */}
      <select
        ref={selectRef}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
        {...props}
      >
        {children}
      </select>

      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm shadow-sm transition focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
          className,
        )}
      >
        <span className="truncate">{selected?.label ?? options[0]?.label ?? ''}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
      </button>

      {open && (
        <div
          className={cn(
            'absolute z-50 w-full min-w-[12rem] rounded-md border border-slate-200 bg-white shadow-lg',
            openUpward ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          {searchable && (
            <div className="relative border-b border-slate-100 p-1.5">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchInputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setOpen(false);
                  } else if (e.key === 'Enter' && filtered.length === 1) {
                    selectOption(filtered[0].value);
                  }
                }}
                placeholder="Search…"
                className="w-full rounded border border-slate-200 py-1 pl-7 pr-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
            </div>
          )}
          <div className="max-h-60 overflow-y-auto py-1">
            {groups.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-400">No matches</div>
            ) : (
              groups.map((g, gi) => (
                <div key={gi}>
                  {g.label && (
                    <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{g.label}</div>
                  )}
                  {g.options.map((o) => (
                    <div
                      key={o.value}
                      onClick={() => !o.disabled && selectOption(o.value)}
                      className={cn(
                        'flex cursor-pointer items-center justify-between px-3 py-1.5 text-sm hover:bg-slate-50',
                        o.value === currentValue && 'bg-brand-50 text-brand-700',
                        o.disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
                      )}
                    >
                      <span className="truncate">{o.label}</span>
                      {o.value === currentValue && <Check className="h-3.5 w-3.5 shrink-0" />}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
});
Select.displayName = 'Select';

interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, className, ...props }, ref) => (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input
        ref={ref}
        type="checkbox"
        {...props}
        className={cn(
          'h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500',
          className,
        )}
      />
      {label}
    </label>
  ),
);
Checkbox.displayName = 'Checkbox';
